import { type FC } from 'react';
import {
    Box,
    Typography,
    ImageList,
    ImageListItem,
    Skeleton,
    IconButton,
    Button
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { ImageWithUrl } from '../hooks/useImageLoader';
import { SiteImages } from '@/types/site.types';

interface ImageGalleryProps {
    images: SiteImages[];
    imagesWithUrls: ImageWithUrl[];
    selectedSite: string | null;
    siteName: string;
    isSuperAdmin: boolean;
    currentUserId: string;
    onImageClick: (loadedUrl: string | null) => void;
    onDeleteImage: (imageUrl: string) => void;
    onClearFilter: () => void;
}


const ImageGallery: FC<ImageGalleryProps> = ({
    images,
    imagesWithUrls,
    selectedSite,
    siteName,
    isSuperAdmin,
    currentUserId,
    onImageClick,
    onDeleteImage,
    onClearFilter
}) => {
    const canDeleteImage = (imageUrl: string): boolean => {
        debugger;
        const image = images.find(img => img.imageUrl === imageUrl);
        if (isSuperAdmin && image && image.user === currentUserId) return true;
        return false;
    };

    const renderFilterInfo = () => {
        if (!selectedSite) return null;

        return (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Mostrando fotos de:{' '}
                    <Typography component="span" fontWeight={600} color="primary.main">
                        {siteName}
                    </Typography>
                </Typography>
            </Box>
        );
    };

    // Show empty state
    if (imagesWithUrls.length === 0) {
        return (
            <>
                {renderFilterInfo()}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No hay imágenes disponibles
                    </Typography>
                    {selectedSite && (
                        <Button
                            variant="text"
                            onClick={onClearFilter}
                            sx={{ mt: 2, textTransform: 'none' }}
                        >
                            Ver todas las imágenes
                        </Button>
                    )}
                </Box>
            </>
        );
    }

    return (
        <>
            {renderFilterInfo()}
            
            <ImageList
                variant="masonry"
                cols={3}
                gap={8}
                sx={{
                    '@media (max-width: 900px)': {
                        gridTemplateColumns: 'repeat(2, 1fr) !important'
                    },
                    '@media (max-width: 600px)': {
                        gridTemplateColumns: 'repeat(1, 1fr) !important'
                    }
                }}
            >
                {imagesWithUrls.map((image, index) => (
                    <ImageListItem
                        key={index}
                        sx={{
                            cursor: image.loadedUrl ? 'pointer' : 'default',
                            position: 'relative',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            borderRadius: 1,
                            overflow: 'hidden',
                            '&:hover': image.loadedUrl ? {
                                transform: 'scale(1.03)',
                                boxShadow: 3,
                                '& .delete-button': {
                                    opacity: 1
                                }
                            } : {}
                        }}
                        onClick={() => onImageClick(image.loadedUrl)}
                    >
                        {/* Delete Button for Image Owner or SuperAdmin */}
                        {canDeleteImage(image.imageUrl) && image.loadedUrl && (
                            <IconButton
                                className="delete-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDeleteImage(image.imageUrl);
                                }}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    backgroundColor: 'rgba(244, 67, 54, 0.9)',
                                    color: 'white',
                                    width: 36,
                                    height: 36,
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease, transform 0.2s ease',
                                    zIndex: 10,
                                    '&:hover': {
                                        backgroundColor: '#d32f2f',
                                        transform: 'scale(1.1)'
                                    }
                                }}
                                size="small"
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        )}

                        {/* Image Loading State */}
                        {image.loading ? (
                            <Skeleton
                                variant="rectangular"
                                width="100%"
                                height={300}
                                sx={{ borderRadius: 1 }}
                            />
                        ) : image.error || !image.loadedUrl ? (
                            <Box
                                sx={{
                                    width: '100%',
                                    height: 300,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: 1
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Error al cargar imagen
                                </Typography>
                            </Box>
                        ) : (
                            <img
                                src={image.loadedUrl}
                                alt={`Campus foto ${index + 1}`}
                                loading="lazy"
                                style={{
                                    borderRadius: '4px',
                                    display: 'block',
                                    width: '100%'
                                }}
                            />
                        )}
                    </ImageListItem>
                ))}
            </ImageList>
        </>
    );
};

export default ImageGallery;
